import { Button, Empty, Input } from "antd";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from "react-plaid-link";
import * as Yup from "yup";
import {
  useCreateAccount,
  usePlaidCompleteConnect,
  useCreatePlaidLinkSession,
} from "../../api/api";
import { useGetAccounts, useGetSubscriptionLimits } from "../../api/api.get";
import DeleteCardDialog from "../../components/dialog/delete-card.dialog";
import { useNotifications } from "../../components/notifications/notification";
import SubscriptionUpgradeComponent from "../../components/subscription-upgrade.component";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useGroupContext } from "../../providers/groups.provider";
import { IAccount } from "../../types/types";
import AccountRow from "./account.row";

export default function Accounts() {
  const { mutateAsync } = useCreateAccount();
  const { defaultGroupId } = useGroupContext();
  const { mutateAsync: createPlaidAccountAsync } = usePlaidCompleteConnect();
  const { data: accounts, refetch: refetchAccounts } = useGetAccounts();

  const { data: subscriptionLimitData } = useGetSubscriptionLimits();
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onExit: (error) => console.error(error),
    onSuccess: (publicToken: string, meta: PlaidLinkOnSuccessMetadata) =>
      handlePlaidAccountCreation(publicToken, meta),
  });

  const { mutateAsync: startPlaidSessionAsync } = useCreatePlaidLinkSession();

  const { notification, notifyError, notifySuccess } = useNotifications();

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken, ready]);

  const handlePlaidAccountCreation = (
    publicToken: string,
    meta: PlaidLinkOnSuccessMetadata,
  ) => {
    createPlaidAccountAsync({
      bankId: meta.institution?.institution_id,
      bankName: meta.institution?.name ?? "",
      groupId: defaultGroupId!,
      token: publicToken,
    })
      .then(() => {
        refetchAccounts();
        notifySuccess({
          title: "Success",
          description: "Successfully connect to your account",
        });
      })
      .catch(() =>
        notifyError(
          "Unable to connect to bank. Please try again or contact the admin",
        ),
      );
  };

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        name: values.name,
      })
        .then(async () => {
          await refetchAccounts();
          notifySuccess({ description: "Account successfully created" });
          formik.resetForm();
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  const handleConnectToBankAccount = () => {
    startPlaidSessionAsync({})
      .then((resp) => {
        setLinkToken(resp.link_token);
      })
      .catch((e) => {
        let msg = "Whoops! Please try again.";
        if (e.response.data.statusCode === 403) {
          msg = e.response.data.message;
        }
        notifyError(msg);
      });
  };

  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState<IAccount>();

  const remainingAllowedPlaidAccount = useMemo(() => {
    if (subscriptionLimitData?.plaidAccounts && accounts) {
      const plaidConnectedAccounts = accounts.filter(
        (acc) => !!acc.plaidAccount,
      ).length;
      return subscriptionLimitData.plaidAccounts - plaidConnectedAccounts;
    }
    return 0;
  }, [subscriptionLimitData, accounts]);

  const handleSuccess = () => {
    notifySuccess({
      title: "Success",
      description: "Account deleted successfully",
    });
    setConfirmDeleteAccount(undefined);
    refetchAccounts();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      <DeleteCardDialog
        open={!!confirmDeleteAccount}
        onError={() => notifyError("whoops! Please try again.")}
        onSuccess={() => handleSuccess()}
        card={confirmDeleteAccount}
        onCancel={() => setConfirmDeleteAccount(undefined)}
      />
      <main className="pt-9 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Accounts
            </h1>
            <p className="text-muted-foreground">
              Manage your connected and individual accounts
            </p>
          </div>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Accounts List */}

            <div className="lg:col-span-2">
              {!accounts ||
                (accounts.length === 0 && (
                  <Empty description="No Accounts Found" className="mt-10" />
                ))}

              {accounts && accounts.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Your Accounts
                    </CardTitle>
                    <CardDescription>
                      {accounts?.length} Account
                      {accounts?.length !== 1 ? "s" : ""}{" "}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {accounts.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground">
                            No Accounts. Create your first!
                          </p>
                        </div>
                      ) : (
                        accounts.map((account) => {
                          return (
                            <AccountRow
                              onDeleteClick={() =>
                                setConfirmDeleteAccount(account)
                              }
                              account={account}
                              onSuccess={(msg: string) => {
                                notifySuccess({
                                  title: "Success",
                                  description: msg,
                                });
                                refetchAccounts();
                              }}
                              OnError={(msg) => {
                                notifyError(msg);
                              }}
                            />
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel: Add Account Form */}
            <div className="flex flex-col gap-8">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Connect To Bank</CardTitle>
                  <CardDescription>
                    Securely connect to your bank account to automatically pull
                    transactions everyday.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Account Name */}

                  {/* Buttons */}
                  <div className="flex justify-between items-center gap-8 mt-4">
                    <Button
                      className="flex-1"
                      onClick={handleConnectToBankAccount}
                      type="primary"
                      size="large"
                      disabled={remainingAllowedPlaidAccount === 0}
                    >
                      Connect
                    </Button>
                    <a
                      href="https://help.expensehut.com/plaid"
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1"
                    >
                      How It Works?
                    </a>
                  </div>

                  {remainingAllowedPlaidAccount === 0 && (
                    <SubscriptionUpgradeComponent />
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Add Manual Account</CardTitle>
                  <CardDescription className="text-xs">
                    Manual accounts are used to separately track your expenses.
                    They are not connected to bank account for auto-sync.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    {/* Account Name */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Account Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Summer Vacation"
                        {...formik.getFieldProps("name")}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Give your account a custom name.
                      </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between gap-2 pt-4">
                      <Button
                        className="flex-1"
                        onClick={formik.submitForm}
                        type="primary"
                        size="large"
                        disabled={Object.keys(formik.errors).length > 0}
                      >
                        Add Account
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={() => formik.resetForm()}
                        type="text"
                        size="large"
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
